import { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UpgradeModal from "@/components/UpgradeModal";
import UpgradePrompt from "@/components/UpgradePrompt";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Info, TrendingUp, TrendingDown, DollarSign, HelpCircle, AlertTriangle } from "lucide-react";

export default function Taxes() {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Check if user has access to tax overview
  const hasTaxAccess = user && (
    user.plan === 'pro' || 
    user.plan === 'pro_monthly' || 
    user.plan === 'pro_yearly' ||
    user.plan === 'lifetime'
  );
  
  // Show upgrade prompt if no access
  if (!hasTaxAccess) {
    return (
      <UpgradePrompt 
        feature={t('upgrade.taxCalculation', 'Steuerberechnung')}
        requiredPlan="Pro"
        currentPlan={user?.plan || 'Free'}
        description={t('upgrade.taxDescription', 'Automatische Berechnung Ihrer Steuerlast basierend auf Ihren Einnahmen und Ausgaben. Unterstützt DE, AT und CH.')}
      />
    );
  }
  const [taxData, setTaxData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [tradeTaxRate, setTradeTaxRate] = useState("3.5");
  const [country, setCountry] = useState("DE");

  useEffect(() => {
    // Get user's country
    if (user && user.country) {
      setCountry(user.country);
    }
    calculateTaxes();
  }, [user]);

  const calculateTaxes = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/tax/calculate?trade_tax_rate=${tradeTaxRate}`
      );
      setTaxData(response.data);
      if (response.data.country) {
        setCountry(response.data.country);
      }
    } catch (error) {
      console.error("Error calculating taxes:", error);
      toast.error(t('taxes.calculationError'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Tooltip component for calculation steps
  const TaxTooltip = ({ step }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    if (!step) return null;
    
    return (
      <div className="relative inline-block">
        <button
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
          onClick={() => setIsOpen(!isOpen)}
          className="ml-2 text-gray-400 hover:text-emerald-600 transition-colors"
          type="button"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
        
        {isOpen && (
          <div className="absolute z-50 left-0 top-6 w-96 p-4 bg-white border border-gray-200 rounded-lg shadow-xl">
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-sm text-gray-900 mb-1">{t('taxes.calculation.formula')}</p>
                <p className="text-xs text-gray-600 font-mono bg-gray-50 p-2 rounded">{step.formula}</p>
              </div>
              
              <div>
                <p className="font-semibold text-sm text-gray-900 mb-1">{t('taxes.calculation.explanation')}</p>
                <p className="text-xs text-gray-700">{step.explanation}</p>
              </div>
              
              {step.limitations && (
                <div className="border-t pt-2">
                  <p className="font-semibold text-sm text-orange-700 mb-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {t('taxes.calculation.limitations')}
                  </p>
                  <p className="text-xs text-orange-600">{step.limitations}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading && !taxData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="taxes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('taxes.taxCalculation')}</h1>
          <p className="text-lg text-gray-600">
            {country === "AT" 
              ? t('taxes.provisionalAustrianTax') 
              : country === "DE"
              ? t('taxes.provisionalGermanTax')
              : t('taxes.taxCalculation')}
          </p>
        </div>
        <a 
          href="/tax-documentation" 
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
        >
          <Info className="h-5 w-5" />
          <span className="font-medium">{t('taxes.howItWorks')}</span>
        </a>
      </div>

      {/* Warning for unsupported countries (only OTHER, not CH) */}
      {country === "OTHER" && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-orange-900">
                <p className="font-semibold mb-2">
                  {t("taxes.noCalculation", "Automatic tax calculation not available")}
                </p>
                <p className="mb-2">
                  {t("taxes.noCalculationText", "No specific tax calculations available for your country.")}
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>{t("taxes.manualEntry", "Bitte tragen Sie Ihre Steuern manuell in Ihre Buchhaltung ein")}</li>
                  <li>{t("taxes.consultTaxAdvisor", "Consult a tax advisor for accurate calculations")}</li>
                  <li>{t("taxes.useGenericVAT", "You can use generic VAT rates in invoices")}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Info for Switzerland - Canton required */}
      {country === "CH" && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-2">
                  🇨🇭 {t("taxes.swissTaxes", "Schweizer Steuerberechnung")}
                </p>
                <p className="mb-2">
                  {t("taxes.swissInfo", "The tax calculation includes federal tax, cantonal tax, and AHV/IV/EO contributions.")}
                </p>
                <p className="text-xs text-blue-700">
                  {t("taxes.swissNote", "Note: Please ensure your canton is correctly selected in settings.")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Banner - General note for all supported countries */}
      {country && ["DE", "AT", "CH"].includes(country) && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">{t('taxes.disclaimer')}</p>
                <p>
                  {t('taxes.disclaimerText')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calculation Controls - Only for supported countries */}
      {country === "DE" && (
        <Card>
          <CardHeader>
            <CardTitle>{t('taxes.parameters.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('taxes.parameters.tradeTaxRate')}</Label>
                <Input
                  data-testid="trade-tax-rate-input"
                  type="number"
                  step="0.1"
                  value={tradeTaxRate}
                  onChange={(e) => setTradeTaxRate(e.target.value)}
                  placeholder="3.5"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('taxes.parameters.tradeTaxRateHint')}
                </p>
              </div>
              <div className="flex items-end">
                <Button
                  data-testid="calculate-taxes-button"
                  onClick={calculateTaxes}
                  disabled={loading}
                  className="w-full"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? t('taxes.parameters.calculating') : t('taxes.parameters.calculate')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {country === "AT" && (
        <Card>
          <CardHeader>
            <CardTitle>{t('taxes.parameters.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <Button
                data-testid="calculate-taxes-button"
                onClick={calculateTaxes}
                disabled={loading}
                className="w-full md:w-auto"
              >
                <Calculator className="h-4 w-4 mr-2" />
                {loading ? t('taxes.parameters.calculating') : t('taxes.parameters.calculate')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {country === "CH" && (
        <Card>
          <CardHeader>
            <CardTitle>{t('taxes.swiss.titleShort')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <Button
                data-testid="calculate-taxes-button"
                onClick={calculateTaxes}
                disabled={loading}
                className="w-full md:w-auto"
              >
                <Calculator className="h-4 w-4 mr-2" />
                {loading ? t('taxes.parameters.calculating') : t('taxes.parameters.calculateTaxes')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {taxData && (
        <>
          {/* Overview Cards - Clean Design */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border border-gray-200 hover:border-gray-300 transition-colors">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  {t('taxes.overview.income')}
                  <TrendingUp className="h-4 w-4 text-gray-400" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">{t('taxes.overview.gross')}</div>
                  <div className="text-2xl font-bold text-gray-900" data-testid="total-income-display">
                    {formatCurrency(taxData.total_income)}
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-0.5">{t('taxes.overview.net')}</div>
                  <div className="text-lg font-semibold text-gray-700">
                    {formatCurrency(taxData.total_income_netto || 0)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-gray-300 transition-colors">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  {t('taxes.overview.expenses')}
                  <TrendingDown className="h-4 w-4 text-gray-400" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">{t('taxes.overview.gross')}</div>
                  <div className="text-2xl font-bold text-gray-900" data-testid="total-expenses-display">
                    {formatCurrency(taxData.total_expenses)}
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-0.5">{t('taxes.overview.net')}</div>
                  <div className="text-lg font-semibold text-gray-700">
                    {formatCurrency(taxData.total_expenses_netto || 0)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-gray-300 transition-colors">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                  {t('taxes.overview.profit')}
                  <Calculator className="h-4 w-4 text-gray-400" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900" data-testid="taxable-income-display">
                  {formatCurrency(taxData.taxable_income)}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('taxes.overview.beforeTaxes')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-900 bg-gray-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center justify-between">
                  {t('taxes.overview.netAvailable')}
                  <DollarSign className="h-4 w-4 text-gray-700" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900" data-testid="net-profit-display">
                  {formatCurrency(taxData.net_profit)}
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  {t('taxes.overview.afterTaxes')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* VAT Breakdown */}
          <Card className="border border-gray-200">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base font-semibold text-gray-900">
                {t('taxes.vat.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="text-xs font-medium text-gray-500 mb-1">{t('taxes.vat.collected')}</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(taxData.vat_collected)}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('taxes.vat.fromIncome')}
                  </p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="text-xs font-medium text-gray-500 mb-1">{t('taxes.vat.paid')}</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(taxData.vat_paid)}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('taxes.vat.fromExpenses')}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 border-2 border-gray-900 rounded-lg">
                  <div className="text-xs font-semibold text-gray-900 mb-1">{t('taxes.vat.payable')}</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(taxData.vat_payable)}
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    {t('taxes.vat.formula')}
                  </p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  <Info className="inline h-4 w-4 mr-1 text-gray-500" />
                  {t('taxes.vat.infoShort')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Removed: Gross/Net Visual Breakdown - replaced by Transparente Berechnung section */}

          {/* Tax Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>{t('taxes.breakdown.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Income Tax */}
                {/* Income Tax - Different display for CH */}
                {country === "CH" ? (
                  <>
                    {/* Federal Tax */}
                    {taxData.federal_tax > 0 && (
                      <div className="border-b pb-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-2">
                            <div>
                              <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.federalTax')}</h3>
                              <p className="text-sm text-gray-600">
                                {t('taxes.breakdown.federalTaxDesc')}
                              </p>
                            </div>
                            {taxData.calculation_steps && taxData.calculation_steps[3] && (
                              <TaxTooltip step={taxData.calculation_steps[3]} />
                            )}
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(taxData.federal_tax)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Cantonal Tax */}
                    {taxData.cantonal_tax > 0 && (
                      <div className="border-b pb-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-2">
                            <div>
                              <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.cantonalTax')}</h3>
                              <p className="text-sm text-gray-600">
                                {taxData.canton_name || t('taxes.breakdown.cantonDefault')}
                              </p>
                            </div>
                            {taxData.calculation_steps && taxData.calculation_steps[4] && (
                              <TaxTooltip step={taxData.calculation_steps[4]} />
                            )}
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(taxData.cantonal_tax)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Total Income Tax for CH */}
                    <div className="border-b pb-4">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.incomeTaxTotal')}</h3>
                          <p className="text-sm text-gray-600">
                            {t('taxes.breakdown.incomeTaxTotalDesc')}
                          </p>
                        </div>
                        <div className="text-2xl font-bold text-gray-900" data-testid="income-tax-display">
                          {formatCurrency(taxData.income_tax)}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.incomeTax')}</h3>
                          <p className="text-sm text-gray-600">
                            {country === "AT" 
                              ? t('taxes.breakdown.incomeTaxAT') 
                              : t('taxes.breakdown.incomeTaxDE')}
                          </p>
                        </div>
                        {taxData.calculation_steps && taxData.calculation_steps[3] && (
                          <TaxTooltip step={taxData.calculation_steps[3]} />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="income-tax-display">
                        {formatCurrency(taxData.income_tax)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Solidarity Surcharge - DE only */}
                {country === "DE" && taxData.solidarity_surcharge >= 0 && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {t('taxes.breakdown.solidaritySurcharge')}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {t('taxes.breakdown.solidaritySurchargeDesc')}
                          </p>
                        </div>
                        {taxData.calculation_steps && taxData.calculation_steps[4] && (
                          <TaxTooltip step={taxData.calculation_steps[4]} />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="solidarity-surcharge-display">
                        {formatCurrency(taxData.solidarity_surcharge)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Trade Tax - DE only */}
                {country === "DE" && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.tradeTax')}</h3>
                          <p className="text-sm text-gray-600">
                            {t('taxes.breakdown.tradeTaxDesc')} {tradeTaxRate}%
                          </p>
                        </div>
                        {taxData.calculation_steps && taxData.calculation_steps[5] && (
                          <TaxTooltip step={taxData.calculation_steps[5]} />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="trade-tax-display">
                        {formatCurrency(taxData.trade_tax)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Social Security - AT and CH */}
                {(country === "AT" || country === "CH") && taxData.social_security > 0 && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {country === "CH" ? t('taxes.breakdown.socialSecurityCH') : t('taxes.breakdown.socialSecurity')}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {country === "CH" 
                              ? t('taxes.swiss.contribution')
                              : t('taxes.breakdown.socialSecurityAT')}
                          </p>
                        </div>
                        {taxData.calculation_steps && (
                          country === "AT" ? taxData.calculation_steps[4] : taxData.calculation_steps[5]
                        ) && (
                          <TaxTooltip step={
                            country === "AT" ? taxData.calculation_steps[4] : taxData.calculation_steps[5]
                          } />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="social-security-display">
                        {formatCurrency(taxData.social_security)}
                      </div>
                    </div>
                  </div>
                )}

                {/* VAT */}
                <div className="pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {t('taxes.breakdown.vatLabel')}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {t('taxes.breakdown.vatDetails')} {formatCurrency(taxData.vat_collected)} | 
                          {t('taxes.breakdown.vatPaid')} {formatCurrency(taxData.vat_paid)}
                        </p>
                      </div>
                      {taxData.calculation_steps && (
                        (country === "DE" && taxData.calculation_steps[6]) ||
                        (country === "AT" && taxData.calculation_steps[5]) ||
                        (country === "CH" && taxData.calculation_steps[6])
                      ) && (
                        <TaxTooltip step={
                          country === "DE" ? taxData.calculation_steps[6] :
                          country === "AT" ? taxData.calculation_steps[5] :
                          taxData.calculation_steps[6]
                        } />
                      )}
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-testid="vat-payable-display">
                      {formatCurrency(taxData.vat_payable)}
                    </div>
                  </div>
                </div>

                {/* Total Tax Burden */}
                <div className="p-4 border-2 border-gray-900 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {t('taxes.burden.total')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {country === "CH" 
                          ? t('taxes.burden.ch')
                          : country === "AT"
                          ? t('taxes.burden.at')
                          : t('taxes.burden.de')}
                      </p>
                    </div>
                    <div className="text-3xl font-bold text-gray-900" data-testid="total-tax-burden-display">
                      {formatCurrency(taxData.total_tax_burden)}
                    </div>
                  </div>
                </div>

                {/* Effective Tax Rate */}
                <div className="p-4 border border-gray-200 bg-white rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {t('taxes.burden.effectiveRate')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('taxes.burden.ratioDescription')}
                      </p>
                    </div>
                    <div className="text-3xl font-bold text-gray-900" data-testid="effective-tax-rate-display">
                      {taxData.taxable_income > 0
                        ? ((taxData.total_tax_burden / taxData.taxable_income) * 100).toFixed(2)
                        : "0.00"}
                      %
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Calculation Steps */}
          {taxData.calculation_steps && taxData.calculation_steps.length > 0 && (
            <Card className="border border-gray-200">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  {t('taxes.calculation.title')}
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {t('taxes.calculation.subtitle')}
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {taxData.calculation_steps.map((step, index) => (
                    <div 
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {step.name}
                          </h3>
                          <div className="text-xs font-mono text-gray-600 bg-gray-50 px-3 py-2 rounded border border-gray-200 mb-2">
                            {step.formula}
                          </div>
                        </div>
                        <div className="text-xl font-bold text-gray-900 ml-4">
                          {formatCurrency(step.amount)}
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-700 mb-2">
                        {step.explanation}
                      </div>
                      
                      {step.limitations && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500 flex items-start gap-1">
                            <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            <span>{step.limitations}</span>
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tax Optimization Tips */}
          <Card className="border border-gray-200">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base font-semibold text-gray-900">
                {t('taxes.tips.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start space-x-3">
                  <div className="h-1.5 w-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong className="text-gray-900">{t('taxes.tips.expenses')}:</strong> {t('taxes.tips.expensesDesc')}
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-1.5 w-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong className="text-gray-900">{t('taxes.tips.depreciation')}:</strong> {t('taxes.tips.depreciationDesc')}
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong>{t('taxes.tips.smallBusiness')}:</strong> {t('taxes.tips.smallBusinessDesc')}
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong>{t('taxes.tips.pension')}:</strong> {t('taxes.tips.pensionDesc')}
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong>{t('taxes.tips.advisor')}:</strong> {t('taxes.tips.advisorDesc')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
